import { TFunction } from 'i18next';

interface BookData {
  id: number;
  title: string;
  picture: string | null;
  description?: string | null;
  slug: string;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_alt?: string | null;
  seo_keywords: string[];
}

interface ChapterData {
  number: number;
  title: string;
  preview?: string;
}

interface BookSEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogImage: string;
  ogType: string;
  alt: string;
}

interface ChapterSEOData extends BookSEOData {}

export const generateBookSEO = (book: BookData, t: TFunction): BookSEOData => {
  const seoTitle = book.seo_title || book.title || t('book_title_default');
  const seoDescription =
    book.seo_description || book.description || t('book_description_default');
  const seoKeywords =
    book.seo_keywords.join(', ') || t('book_keywords_default');
  const seoAlt = book.seo_alt || book.title || t('book_title_default');

  return {
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
    canonical: `/books/${book.slug}`,
    ogImage: book.picture || '/img/favicon.png',
    ogType: 'article',
    alt: seoAlt,
  };
};

export const generateChapterSEO = (
  book: BookData,
  chapter: ChapterData,
  t: TFunction
): ChapterSEOData => {
  const chapterTitle = chapter.title
    ? `${t('chapter_label', { number: chapter.number })}: ${chapter.title}`
    : t('chapter_label', { number: chapter.number });

  const seoTitle = `${chapterTitle} - ${book.title}`;

  const seoDescription =
    chapter.preview ||
    book.seo_description ||
    book.description ||
    t('chapter_description_default', {
      chapterNumber: chapter.number,
      bookTitle: book.title,
    });

  const seoKeywords =
    book.seo_keywords.join(', ') || t('book_keywords_default');

  const seoAlt = `${chapterTitle} - ${book.title}`;

  return {
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
    canonical: `/books/${book.slug}/chapter/${chapter.number}`,
    ogImage: book.picture || '/img/favicon.png',
    ogType: 'article',
    alt: seoAlt,
  };
};
